/**
 * El manuscrito, dentro del Studio.
 *
 * Hasta ahora el autor tenía que pegar el texto en una caja para que un colaborador lo tocara.
 * Eso es una copia: se desincroniza al instante y nadie sabe cuál es la buena. Aquí se abre el
 * documento de su disco, se trabaja sobre él, y sigue siendo el mismo fichero.
 */
import { useState } from "react";
import { documento, miles } from "../api";
import type { Fichero } from "../api";
import { documentosVisibles, papelDeFichero } from "../logica";

export default function Manuscrito(
  { ficheros, abierto, alAbrir, doc, alElegir }: {
    ficheros: Fichero[];
    doc?: string | null;
    abierto: { ruta: string; texto: string } | null;
    alAbrir: (d: { ruta: string; texto: string } | null) => void;
    /** Trabajar con otro documento de la carpeta (Rais, 22-sep: «no puedo cambiar el manuscrito»). */
    alElegir?: (ruta: string) => void;
  },
) {
  const [cargando, setCargando] = useState<string | null>(null);

  // Ni la carpeta entera ni en orden alfabético: el elegido primero, y el resto se busca.
  const [filtro, setFiltro] = useState("");
  const vista = documentosVisibles(ficheros, { doc, abierto: abierto?.ruta, filtro });
  const [fallo, setFallo] = useState<string | null>(null);

  async function abrir(f: Fichero) {
    if (abierto?.ruta === f.path) { alAbrir(null); return; }   // segundo clic: se cierra
    setCargando(f.path);
    setFallo(null);
    try {
      const r = await documento(f.path);
      if ("error" in r) throw new Error(r.error);
      alAbrir({ ruta: r.path, texto: r.text });
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se pudo abrir el documento.");
    } finally {
      setCargando(null);
    }
  }

  return (
    <section className="carta">
      <h2>Tu manuscrito
        <span className="sub">
          {abierto ? `${miles(abierto.texto.split(/\s+/).length)} palabras abiertas` : `${ficheros.length} documentos`}
        </span>
      </h2>

      {vista.muchos && (
        <p className="vacio">
          Esta carpeta tiene {miles(vista.total)} documentos: parece Descargas o Documentos, no la carpeta de un
          libro. En <b>Proyectos</b> puedes empezar uno con la carpeta del libro, y el equipo trabajará solo con lo suyo.
        </p>
      )}
      {vista.recortada && (
        <input
          className="escribir"
          placeholder={`Buscar entre ${miles(vista.total)} documentos…`}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      )}
      {vista.recortada && !vista.fijos.length && !filtro.trim() && (
        <p className="vacio">Busca tu manuscrito por su nombre y ábrelo.</p>
      )}
      {vista.recortada && filtro.trim() && !vista.buscados.length && (
        <p className="vacio">Ningún documento se llama así.</p>
      )}

      <div className="equipo">
        {[...vista.fijos, ...vista.buscados].map((f) => (
          <div key={f.path} className="cola-con-accion">
          <button
            className={"cola" + (abierto?.ruta === f.path ? " elegido" : "")}
            onClick={() => abrir(f)}
          >
            <span className="cara" style={{ background: "var(--sup3)", color: "var(--mut)" }}>
              {f.ext.replace(".", "").slice(0, 3).toUpperCase()}
            </span>
            <span className="n">
              <b>{f.name}</b>
              {/* Qué es cada uno: cuál se abre en Word y cuál solo se lee (Rais, 22-sep). */}
              <span>{cargando === f.path ? "abriendo…"
                : `${papelDeFichero(f.path, doc) ?? ""}${papelDeFichero(f.path, doc) ? " · " : ""}${Math.round(f.size / 1024)} KB`}</span>
            </span>
          </button>
          {/* Cambiar de manuscrito sin salir de aquí: hasta hoy solo se podía elegir cuando no
              había ninguno, así que quien se equivocaba se quedaba atrapado (Rais, 22-sep). */}
          {alElegir && /\.docx$/i.test(f.name) && f.path !== doc && !/^(informe|plan|registro)-/i.test(f.name) && (
            <button className="btn texto" onClick={() => alElegir(f.path)}>Trabajar con este</button>
          )}
          </div>
        ))}
      </div>

      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}

      {abierto && (
        <div className="hoja">
          {abierto.texto.slice(0, 6000)}
          {abierto.texto.length > 6000 && <span className="vacio">\n\n… y {miles(abierto.texto.length - 6000)} caracteres más.</span>}
        </div>
      )}
    </section>
  );
}
